import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { retryUnlessDeterministic } from '@/lib/query/queryClient';

export interface ErrorStateProps {
  /** The rejection itself (typically `query.error`), so the message goes through the one error reader. */
  error: unknown;
  /** i18n key shown when the response carried no reason of its own. */
  fallbackKey?: string;
  /**
   * Retry action (typically `query.refetch`). Offered only when trying again can change the outcome:
   * a deterministic 4xx (anything but 408/429) hides it, because it would fail identically.
   */
  onRetry?: () => unknown;
  className?: string;
}

/**
 * The shared "this failed to load" surface: `ApiErrorAlert` plus the retry policy every load-error
 * site should share. A failed load must render through this, never as an empty list: a zero-record
 * read is "I don't know", not "nothing is wrong".
 */
export function ErrorState({
  error,
  fallbackKey = 'common.errors.loadFailed',
  onRetry,
  className,
}: ErrorStateProps) {
  return (
    <ApiErrorAlert
      error={error}
      fallbackKey={fallbackKey}
      onRetry={onRetry ? retryUnlessDeterministic(error, onRetry) : undefined}
      className={className}
    />
  );
}
